import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'batiboys_access_token';
const REFRESH_KEY = 'batiboys_refresh_token';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function saveTokens(token, refreshToken) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  } catch (e) { console.error('localStorage save failed:', e.message); }
}

function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) { console.error('localStorage read failed:', e.message); return null; }
}

function getStoredRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY); } catch (e) { console.error('localStorage read failed:', e.message); return null; }
}

function clearTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch (e) { console.error('localStorage clear failed:', e.message); }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRefreshing = useRef(false);

  // Axios request interceptor: attach Authorization header from localStorage
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const token = getStoredToken();
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      config.withCredentials = true;
      return config;
    });

    // Axios response interceptor: auto-refresh on 401
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/login') &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/register')
        ) {
          originalRequest._retry = true;
          if (!isRefreshing.current) {
            isRefreshing.current = true;
            try {
              const refreshToken = getStoredRefreshToken();
              const headers = refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {};
              const res = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
                withCredentials: true,
                headers
              });
              if (res.data.token) {
                saveTokens(res.data.token, null);
              }
              isRefreshing.current = false;
              // Retry original request with new token
              const newToken = getStoredToken();
              if (newToken) {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              }
              return axios(originalRequest);
            } catch (refreshError) {
              isRefreshing.current = false;
              clearTokens();
              setUser(null);
              return Promise.reject(refreshError);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const checkAuth = useCallback(async () => {
    // Skip auth checks entirely on public ST portal pages
    if (typeof window !== 'undefined' && (window.location.pathname.startsWith('/st/offer/') || window.location.pathname.startsWith('/st/demo') || window.location.pathname.startsWith('/client/demo') || window.location.pathname.startsWith('/client/reserves/'))) {
      setUser(null);
      setLoading(false);
      return;
    }
    // Consume an impersonation token from the URL (admin "View as" flow)
    try {
      const params = new URLSearchParams(window.location.search);
      const impToken = params.get('imp');
      if (impToken) {
        saveTokens(impToken, null);
        // Strip the param so a reload doesn't double-consume
        params.delete('imp');
        const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
        window.history.replaceState({}, '', cleanUrl);
      }
    } catch { /* noop */ }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Try refresh before giving up
      try {
        const refreshToken = getStoredRefreshToken();
        const headers = refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {};
        const res = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
          withCredentials: true,
          headers
        });
        if (res.data.token) saveTokens(res.data.token, null);
        const meRes = await axios.get(`${API_URL}/api/auth/me`);
        setUser(meRes.data);
      } catch (refreshError) {
        clearTokens();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    if (response.data.token) {
      saveTokens(response.data.token, response.data.refresh_token);
    }
    setUser(response.data);
    return response.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    if (response.data.token) {
      saveTokens(response.data.token, response.data.refresh_token);
    }
    setUser(response.data);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) { console.error('Logout API call failed:', e.message); }
    clearTokens();
    setUser(null);
  }, []);

  const contextValue = useMemo(() => ({ user, setUser, loading, login, register, logout, checkAuth }), [user, loading, login, register, logout, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
